import flatpickr from "flatpickr"; // Only for types if needed, or remove if not using types
import { Disclosure } from "../core/disclosure";
import { PluginRegistry } from "../core/pluginRegistry";

/**
 * FilterCalendar Plugin
 * Combines Disclosure behavior (show/hide calendar) with CoreCalendar events.
 * Updates two inputs (start/end) and emits detailed change events.
 */
export class FilterCalendar extends Disclosure {
  private startInput: HTMLInputElement | null = null;
  private endInput: HTMLInputElement | null = null;

  // Track previous values to emit "old vs new"
  private previousStartValue: string = "";
  private previousEndValue: string = "";

  private documentClickHandler: (event: MouseEvent) => void;

  constructor(element: HTMLElement) {
    super(element, {
      triggerSelector: "[data-trigger='calendar']",
      contentSelector: "[data-content='calendar']",
      eventNameRaw: "filterCalendar",
    });
    this.documentClickHandler = this.handleDocumentClick.bind(this);
  }

  init(): void {
    this.initDisclosure();

    this.startInput = this.element.querySelector("[data-input='start'] input");
    this.endInput = this.element.querySelector("[data-input='end'] input");

    if (this.startInput) {
      this.previousStartValue = this.startInput.value;
      this.startInput.readOnly = true;
    }
    if (this.endInput) {
      this.previousEndValue = this.endInput.value;
      this.endInput.readOnly = true;
    }

    this.bindEvents();
    this.bindInputEvents(); // Optional: if we want inputs to trigger things

    document.addEventListener("click", this.documentClickHandler);

    // Initialize from data attributes if present
    const dataStart = this.element.dataset.start;
    const dataEnd = this.element.dataset.end;

    if (dataStart || dataEnd) {
      if (dataStart && this.startInput) {
        this.startInput.value = dataStart;
        this.previousStartValue = dataStart;
      }
      if (dataEnd && this.endInput) {
        this.endInput.value = dataEnd;
        this.previousEndValue = dataEnd;
      }

      // Sync CoreCalendar (needs short delay to ensure CoreCalendar is ready)
      setTimeout(() => {
        const dates = [dataStart, dataEnd].filter(Boolean);
        if (dates.length > 0) {
          const calendarContainer = this.element.querySelector(
            "[data-content='calendar'] .core-calendar-container",
          );
          if (calendarContainer) {
            calendarContainer.dispatchEvent(
              new CustomEvent("coreCalendar:setDates", {
                bubbles: true,
                detail: { dates },
              }),
            );
          }
        }
      }, 0);
    }
  }

  protected bindDisclosureEvents(): void {
    const triggers = this.element.querySelectorAll(
      this.options.triggerSelector || "",
    );
    triggers.forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.expand();
      });
    });
  }

  /** Duración del cierre en ms; debe coincidir con la animación en TktDispo (0.4s) para que cortina y aria-hidden vayan a la vez */
  private static readonly COLLAPSE_ANIMATION_MS = 400;

  public collapse(): void {
    if (!this.isExpanded()) return;
    this.element.setAttribute("aria-expanded", "false");
    this.trigger?.setAttribute("aria-expanded", "false");
    setTimeout(() => {
      if (this.content) {
        if (this.content.contains(document.activeElement)) {
          this.trigger?.focus();
        }
        this.content.setAttribute("aria-hidden", "true");
      }
      this.dispatchDisclosureEvent("collapsed");
    }, FilterCalendar.COLLAPSE_ANIMATION_MS);
  }

  private handleDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    const isContentClick = this.content?.contains(target);

    const triggers = this.element.querySelectorAll(
      this.options.triggerSelector || "",
    );
    let isTriggerClick = false;
    triggers.forEach((trigger) => {
      if (trigger.contains(target)) {
        isTriggerClick = true;
      }
    });

    if (!isTriggerClick && !isContentClick && this.isExpanded()) {
      this.collapse();
    }
  }

  public destroy(): void {
    super.destroy();
    document.removeEventListener("click", this.documentClickHandler);
  }

  private bindEvents(): void {
    // Listen for events from the nested CoreCalendar (bubbling up)
    this.element.addEventListener("coreCalendar:change", (e: Event) => {
      const customEvent = e as CustomEvent;
      const { selectedDates, instance } = customEvent.detail;
      this.handleCalendarChange(selectedDates, instance);
    });

    // Listen for reset requests
    this.element.addEventListener("filterCalendar:reset", ((e: CustomEvent) => {
      const silent = e.detail?.silent ?? false;
      this.reset({ silent });
    }) as EventListener);

    // Listen for state requests
    this.element.addEventListener("filterCalendar:requestState", () => {
      this.element.dispatchEvent(
        new CustomEvent("filterCalendar:state", {
          bubbles: true,
          detail: {
            start: this.startInput?.value || "",
            end: this.endInput?.value || "",
          },
        }),
      );
    });

    this.element.addEventListener("filterCalendar:setState", ((
      e: CustomEvent,
    ) => {
      const {
        start,
        end,
        silent = false,
      } = e.detail as { start: string; end: string; silent?: boolean };

      if (this.startInput && start !== undefined) {
        this.previousStartValue = this.startInput.value;
        this.startInput.value = start;
        this.previousStartValue = start;
      }
      if (this.endInput && end !== undefined) {
        this.previousEndValue = this.endInput.value;
        this.endInput.value = end;
        this.previousEndValue = end;
      }

      const dates = [start, end].filter(Boolean);
      if (dates.length > 0) {
        const calendarContainer = this.element.querySelector(
          "[data-content='calendar'] .core-calendar-container",
        );
        if (calendarContainer) {
          calendarContainer.dispatchEvent(
            new CustomEvent("coreCalendar:setDates", {
              bubbles: true,
              detail: { dates, silent },
            }),
          );
        }
      }
    }) as EventListener);
  }

  private bindInputEvents(): void {
    // Optional: make inputs triggers
    // Handled by bindDisclosureEvents if they match selector
  }

  private handleCalendarChange(
    selectedDates: Date[],
    instance: flatpickr.Instance,
  ): void {
    if (!this.startInput || !this.endInput) return;

    const dateFormat = instance.config.dateFormat || "d/m/Y";
    const sortedDates = selectedDates.sort((a, b) => a.getTime() - b.getTime());

    const startDate = sortedDates[0]
      ? flatpickr.formatDate(sortedDates[0], dateFormat)
      : "";
    const endDate = sortedDates[1]
      ? flatpickr.formatDate(sortedDates[1], dateFormat)
      : "";

    if (startDate !== this.startInput.value) {
      this.startInput.value = startDate;
    }
    if (endDate !== this.endInput.value) {
      this.endInput.value = endDate;
    }
    if (sortedDates.length === 2) {
      if (startDate !== this.previousStartValue) {
        this.emitChangeEvent("start", this.previousStartValue, startDate);
        this.previousStartValue = startDate;
        this.startInput.dispatchEvent(new Event("input", { bubbles: true }));
        this.startInput.dispatchEvent(new Event("change", { bubbles: true }));
      }

      if (endDate !== this.previousEndValue) {
        this.emitChangeEvent("end", this.previousEndValue, endDate);
        this.previousEndValue = endDate;
        this.endInput.dispatchEvent(new Event("input", { bubbles: true }));
        this.endInput.dispatchEvent(new Event("change", { bubbles: true }));
      }

      setTimeout(() => this.collapse(), 100);
    }
  }

  private reset(options?: { silent?: boolean }): void {
    const silent = options?.silent ?? false;
    if (this.startInput) {
      this.startInput.value = "";
      this.previousStartValue = "";
    }
    if (this.endInput) {
      this.endInput.value = "";
      this.previousEndValue = "";
    }

    // Reset CoreCalendar
    const calendarContainer = this.element.querySelector(
      "[data-content='calendar'] .core-calendar-container",
    );
    if (calendarContainer) {
      calendarContainer.dispatchEvent(
        new CustomEvent("coreCalendar:reset", {
          bubbles: true,
          detail: { silent },
        }),
      );
    }
  }

  private emitChangeEvent(
    type: "start" | "end",
    oldValue: string,
    newValue: string,
  ): void {
    this.element.dispatchEvent(
      new CustomEvent("filterCalendar:change", {
        bubbles: true,
        detail: {
          type,
          oldValue,
          newValue,
          isStartDate: type === "start",
          isEndDate: type === "end",
        },
      }),
    );
  }
}

PluginRegistry.register("filterCalendar", FilterCalendar);
